import { IAttendanceAutomationChange, IAttendanceBreak, IUserAttendance } from './user-attendance.model';
import { IAttendancePolicy } from '@app/setup/model/attendance-settings.model';
import { calculatePaidBreakTime, calculateUserAttendanceTrackedTime } from '@carlos-orgos/kenjo-shared-libs';
import * as check from 'check-types';

type AttendanceLimitKey = 'breakReminder' | 'maxHoursPerDay';

export async function checkLimits(
  userProfile: string = '',
  attendanceEntry: IUserAttendance,
  attendanceOfTheDay: Array<IUserAttendance>,
  attendancePolicy: IAttendancePolicy,
  checkBreakReminderFn?: (entry: IUserAttendance, todayAttendance: Array<IUserAttendance>) => Promise<{ openReminder: boolean }>,
  checkAutoDeductBreakFn?: (attendanceEntry: IUserAttendance, attendancePolicy: IAttendancePolicy) => Promise<IUserAttendance | undefined>
): Promise<AttendanceLimit | undefined> {
  const isAdmin = ['admin', 'hr-admin'].includes(userProfile);

  const attendanceWithoutTimeOff = (attendanceOfTheDay ?? []).filter(
    (attendance) => !attendance?._allowanceType || attendance?._allowanceType === 'Paid'
  );

  if (checkBreakReminderFn) {
    const breakReminderLimit = new BreakReminderAttendanceLimit(attendanceEntry, attendancePolicy);
    const breakReminderStatus = await breakReminderLimit.checkLimit(attendanceWithoutTimeOff, checkBreakReminderFn);
    if (breakReminderStatus?.shouldShow) {
      return breakReminderLimit;
    }
  }

  const updatedAttendanceEntry = await checkAutoDeductBreakFn(attendanceEntry, attendancePolicy);

  const updatedAttendanceWithoutTimeOff = updatedAttendanceEntry
    ? attendanceWithoutTimeOff.map((entry) => (entry._id === updatedAttendanceEntry._id ? updatedAttendanceEntry : entry))
    : attendanceWithoutTimeOff;

  const maxHoursPerDayLimit = new MaxHoursPerDayAttendanceLimit(updatedAttendanceEntry ?? attendanceEntry, attendancePolicy);
  const maxHoursPerDayStatus = maxHoursPerDayLimit.checkLimit(updatedAttendanceWithoutTimeOff);

  if (isAdmin && maxHoursPerDayStatus?.trackedWithoutCurrent >= attendancePolicy?.limitDailyHours?.limit) {
    return undefined;
  }

  if (maxHoursPerDayStatus?.broken) {
    return maxHoursPerDayLimit;
  }

  return undefined;
}

export function checkMaxHoursPerDay(
  attendanceEntry: IUserAttendance,
  attendanceOfTheDay: Array<IUserAttendance>,
  attendancePolicy: IAttendancePolicy
) {
  if (!attendanceEntry || !attendancePolicy) {
    return undefined;
  }
  const maxHoursPerDayLimit = new MaxHoursPerDayAttendanceLimit(attendanceEntry, attendancePolicy);
  const attendanceWithoutTimeOff = (attendanceOfTheDay ?? []).filter(
    (attendance) =>
      attendance && (!attendance?._allowanceType || attendance?._allowanceType === 'Paid') && attendance?._id !== attendanceEntry?._id
  );
  const allAttendance = [...attendanceWithoutTimeOff, attendanceEntry];
  const maxHoursPerDayStatus = maxHoursPerDayLimit.checkLimit(allAttendance);
  if (maxHoursPerDayStatus?.broken) {
    return maxHoursPerDayLimit;
  }
  return undefined;
}

export function cleanupInvalidBreaks(userAttendance: IUserAttendance): IUserAttendance {
  if (!userAttendance?.breaks || !Array.isArray(userAttendance.breaks) || !check.assigned(userAttendance.endTime)) {
    return userAttendance;
  }
  const changes: Array<IAttendanceAutomationChange> = [];
  const cleanedBreaks = userAttendance.breaks
    .filter((breakItem) => {
      // Remove breaks that end after the endTime
      if (check.assigned(breakItem.end) && breakItem.end >= userAttendance.endTime) {
        changes.push(addAutoDeductBreakToTimeTracking(breakItem, undefined));
        return false;
      }
      return true;
    })
    .map((breakItem) => {
      if (check.assigned(breakItem.end) && breakItem.end > userAttendance.endTime) {
        changes.push(
          addAutoDeductBreakToTimeTracking(breakItem, {
            start: breakItem.start,
            end: userAttendance.endTime,
          })
        );
        return {
          ...breakItem,
          end: userAttendance.endTime,
        };
      }
      return breakItem;
    });
  userAttendance.breakTime = recalculateTotalBreakTime({ ...userAttendance, breaks: cleanedBreaks });

  return {
    ...userAttendance,
    breaks: cleanedBreaks,
  };
}

function recalculateTotalBreakTime(attendanceEntry: IUserAttendance) {
  if (!attendanceEntry?.breaks?.length) {
    return 0;
  }
  return attendanceEntry.breaks.reduce((total, breakItem) => total + (breakItem.end - breakItem.start), 0);
}

export function addAutoDeductBreakToTimeTracking(iBreak: IAttendanceBreak, iBreakNewValue: IAttendanceBreak | undefined) {
  const baseHistoryDocument = {
    operationType: 'Update',
    fieldChanged: 'breaks',
    _createdAt: new Date(),
  };

  const change = {
    ...baseHistoryDocument,
    oldValue: `${iBreak?.start}-${iBreak?.end ?? ''}`,
    newValue: iBreakNewValue ? `${iBreakNewValue?.start}-${iBreakNewValue?.end ?? ''}` : '',
  };

  return change as IAttendanceAutomationChange;
}

export function getMaxEndTime(currentEntry: IUserAttendance, endTimeLimit: number) {
  if (!currentEntry?.breaks || currentEntry?.breaks?.length === 0) {
    return endTimeLimit;
  }

  const sortedBreaks = currentEntry.breaks.sort((a, b) => a.start - b.start);
  let extraBreakTime = 0;
  for (const breakItem of sortedBreaks) {
    if (breakItem.end <= endTimeLimit + extraBreakTime) {
      extraBreakTime += breakItem.end - breakItem.start;
      continue;
    }
    break;
  }
  return endTimeLimit + extraBreakTime;
}

export class AttendanceLimit {
  public key: AttendanceLimitKey = 'breakReminder';
  public newEndTime?: number;
  public allowsSaving: boolean = true;
  public modalTitleKey: string = '';
  public modalTextKey: string = '';
  public modalData: Record<string, any>;

  constructor(public attendanceEntry: IUserAttendance, public attendancePolicy: IAttendancePolicy) {}

  getEntryDuration(entry?: IUserAttendance) {
    const attendanceEntry = entry || this.attendanceEntry;
    if (attendanceEntry.endTime === undefined || attendanceEntry.startTime === undefined) {
      return 0;
    }

    const breakDuration = attendanceEntry.breaks
      ? attendanceEntry.breaks.reduce((total, iBreak) => total + (iBreak.end !== undefined ? iBreak.end - iBreak.start : 0), 0)
      : attendanceEntry.breakTime ?? 0;

    return attendanceEntry.endTime - attendanceEntry.startTime - breakDuration;
  }
}

export class BreakReminderAttendanceLimit extends AttendanceLimit {
  key: AttendanceLimitKey = 'breakReminder';
  public modalTitleKey: string = 'breakReminderTitle';
  public modalTextKey: string = 'breakReminderText';
  public allowsSaving: boolean = false; // Break reminder should prevent saving until handled

  async checkLimit(
    todayAttendance: Array<IUserAttendance>,
    checkBreakReminderFn: (entry: IUserAttendance, todayAttendance: Array<IUserAttendance>) => Promise<{ openReminder: boolean }>
  ) {
    const result = await checkBreakReminderFn(this.attendanceEntry, todayAttendance);
    return {
      shouldShow: result.openReminder,
    };
  }
}

export class MaxHoursPerDayAttendanceLimit extends AttendanceLimit {
  key: AttendanceLimitKey = 'maxHoursPerDay';
  public modalTitleKey: string = 'maxHoursPerDayTitle';
  public modalTextKey: string = 'maxHoursPerDayText';

  checkLimit(todayAttendance: Array<IUserAttendance>) {
    if (!this.attendanceEntry || !this.attendancePolicy) {
      return undefined;
    }
    const limitDailyHours = this.attendancePolicy.limitDailyHours;
    if (!limitDailyHours?.isActive || limitDailyHours?.conflicts) {
      return undefined;
    }

    const otherEntries = todayAttendance.filter((entry) => entry._id !== this.attendanceEntry._id && !entry.afterAutoCreated);

    const otherEntriesTracked = otherEntries.reduce((total, entry) => total + calculateUserAttendanceTrackedTime(entry), 0);
    const totalTracked = todayAttendance.reduce((total, entry) => total + calculateUserAttendanceTrackedTime(entry), 0);

    const totalTrackedWithoutCurrent = otherEntries.reduce((total, entry) => total + calculateUserAttendanceTrackedTime(entry), 0);

    const { limit } = limitDailyHours;
    const difference = totalTracked - limit;
    const timeToAdd = Math.abs(limit - otherEntriesTracked);
    const newEndTime = getMaxEndTime(this.attendanceEntry, this.attendanceEntry.startTime + timeToAdd);
    const cleanedAttendanceEntry = cleanupInvalidBreaks({
      ...this.attendanceEntry,
      endTime: newEndTime,
    }) as IUserAttendance;
    const paidBreakTimeAfterCleanup =
      calculatePaidBreakTime(this.attendancePolicy, {
        breakTime: cleanedAttendanceEntry.breakTime,
        date: new Date(this.attendanceEntry.date),
      }) ?? 0;

    this.newEndTime = newEndTime - paidBreakTimeAfterCleanup;
    this.modalData = { newEndTime: this.newEndTime, maxHoursPerDay: limit, breaks: cleanedAttendanceEntry.breaks };
    return {
      broken: difference > 0,
      difference,
      trackedWithoutCurrent: totalTrackedWithoutCurrent,
    };
  }
}
